import React, { useState, useMemo, useEffect } from 'react';
import { SettingsIcon, DollarSignIcon, BarChartIcon, UsersIcon, AwardIcon, SlidersHorizontalIcon, PlusCircleIcon, XCircleIcon, LightbulbIcon } from '../lib/icons';
import { Card, CardHeader, Input, Select, Button, LineChart } from './ui';
import { AUCTION_TYPES, COMPETITOR_TYPES, PROJECT_TYPES, COST_STRUCTURES, CURRENCIES } from '../lib/constants';
import { runMultiAttributeSimulation } from '../lib/simulation';

export default function Dashboard() {
    const [activeTooltip, setActiveTooltip] = useState(null);

    // State for Bidder Dashboard
    const [auctionType, setAuctionType] = useState('FIRST_PRICE');
    const [currency, setCurrency] = useState('USD');
    const [projectType, setProjectType] = useState('PV_SOLAR');
    const [useAdvancedCost, setUseAdvancedCost] = useState(true);
    const [simpleCost, setSimpleCost] = useState(150000000);
    const [costs, setCosts] = useState(Object.fromEntries(Object.entries(COST_STRUCTURES.PV_SOLAR).map(([k, v]) => [k, v * 1000000])));
    
    useEffect(() => {
        const newCosts = COST_STRUCTURES[projectType];
        setCosts(Object.fromEntries(Object.entries(newCosts).map(([k, v]) => [k, v * 1000000])));
    }, [projectType]);

    useEffect(() => {
        setSimulationResults(null);
    }, [auctionType]);

    const [costUncertainty, setCostUncertainty] = useState(15_000_000); // Epsilon

    const [competitors, setCompetitors] = useState([
        { type: 'AGGRESSIVE', count: 2, costMean: 155_000_000, costStdDev: 5_000_000 },
        { type: 'ESTABLISHED', count: 2, costMean: 160_000_000, costStdDev: 8_000_000 },
    ]);
    
    const [multiAttribute, setMultiAttribute] = useState({
        priceWeight: 80,
        techScore: 90,
        expScore: 95,
    });

    const [simulationResults, setSimulationResults] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [showMultiAttributeHelp, setShowMultiAttributeHelp] = useState(false);

    const myCostEstimate = useMemo(() => useAdvancedCost ? Object.values(costs).reduce((a, b) => a + b, 0) : simpleCost, [costs, useAdvancedCost, simpleCost]);

    const numBidders = useMemo(() => competitors.reduce((sum, comp) => sum + comp.count, 0) + 1, [competitors]);
    
    const correctedCost = useMemo(() => {
        if (numBidders <= 1) return myCostEstimate;
        const n = Number(numBidders);
        const s = Number(myCostEstimate);
        const epsilon = Number(costUncertainty);
        
        // Apply Winner's Curse correction for procurement auctions
        // In procurement: if you win by bidding too low, you likely underestimated costs
        // So you should bid HIGHER than your cost estimate to avoid losses
        return s + ((n - 1) / (n + 1)) * epsilon;
    }, [myCostEstimate, costUncertainty, numBidders]);

    // Optimal bid calculation (commented out as it's not currently used in the UI)
    // const optimalBid = useMemo(() => {
    //     if (auctionType === 'ENGLISH' || auctionType === 'VICKREY' || numBidders <= 1) {
    //         return correctedCost;
    //     }
    //     // First Price and Dutch are strategically equivalent
    //     const n = Number(numBidders);
    //     const v = correctedCost;
    //     return v * (n / (n - 1));
    // }, [correctedCost, numBidders, auctionType]);
    
    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: CURRENCIES[currency].name,
            maximumFractionDigits: 0,
        }).format(value);
    };

    const handleCostChange = (key, value) => {
        setCosts(prev => ({ ...prev, [key]: Number(value) || 0 }));
    };
    
    const handleCompetitorChange = (index, field, value) => {
        const newCompetitors = [...competitors];
        if (field === 'type') {
            newCompetitors[index][field] = value;
        } else {
            newCompetitors[index][field] = Number(value);
        }
        setCompetitors(newCompetitors);
    };
    
    const addCompetitorProfile = () => {
        setCompetitors([...competitors, { type: 'AGGRESSIVE', count: 1, costMean: 150_000_000, costStdDev: 5_000_000 }]);
    };
    
    const removeCompetitorProfile = (index) => {
        setCompetitors(competitors.filter((_, i) => i !== index));
    };

    const handleRunSimulation = () => {
        setIsLoading(true);
        setSimulationResults(null);

        // Check if we have too many bidders
        if (numBidders > 100) {
            alert('Too many bidders. Please reduce the number of competitors to 100 or fewer for optimal performance.');
            setIsLoading(false);
            return;
        }

        // Dynamically adjust simulation count for performance with improved scaling
        const numSims = numBidders > 50 ? 1000 : 
                       numBidders > 30 ? 2000 : 
                       numBidders > 20 ? 3000 : 
                       numBidders > 10 ? 4000 : 5000;

        // Use requestAnimationFrame for better performance and non-blocking execution
        const runSimulationAsync = async () => {
            try {
                const results = [];
                // Test bid range around corrected cost for realistic results
                const bidRange = myCostEstimate * 0.3;
                const bidStart = correctedCost * 0.98;
                const totalSteps = 21;
                
                // Debug log for troubleshooting
                console.log(`Simulation setup - Bidders: ${numBidders}, Corrected Cost: ${correctedCost}, Range: ${bidStart} to ${bidStart + bidRange}`);

                for (let i = 0; i <= 20; i++) {
                    const myBid = bidStart + (bidRange / 20) * i;
                    
                    // Yield control to browser between heavy computations
                    if (i > 0 && i % 5 === 0) {
                        await new Promise(resolve => setTimeout(resolve, 1));
                    }
                    
                    const simResult = runMultiAttributeSimulation({
                        myBid,
                        myTrueCost: correctedCost,
                        myScores: { tech: multiAttribute.techScore, exp: multiAttribute.expScore },
                        competitors,
                        priceWeight: multiAttribute.priceWeight,
                        numSimulations: numSims,
                        auctionType,
                    });
                    
                    let profit = 0;
                    if (simResult.winRate > 0 && simResult.averageWinningPayment > 0) {
                        // For first-price/Dutch: profit = payment - true cost (payment = my bid)
                        // For English/Vickrey: profit = payment - true cost (payment = second highest bid)
                        profit = simResult.averageWinningPayment - correctedCost;
                    }

                    const expectedProfit = simResult.winRate * profit;
                    
                    results.push({
                        bid: myBid,
                        winProbability: simResult.winRate,
                        expectedProfit: expectedProfit,
                    });
                    
                    // Debug log for first few iterations
                    if (i < 3) {
                        console.log(`Bid ${myBid.toFixed(0)}: Win Rate ${(simResult.winRate*100).toFixed(1)}%, Payment ${simResult.averageWinningPayment.toFixed(0)}, Profit ${profit.toFixed(0)}, Expected Profit ${expectedProfit.toFixed(0)}`);
                    }
                }
                setSimulationResults(results);
            } catch (error) {
                console.error('Simulation error:', error);
                setSimulationResults([]);
            }
            setIsLoading(false);
        };

        runSimulationAsync();
    };
    
    const summaryData = useMemo(() => {
        if (!simulationResults) return { firstPrice: null, english: null };

        const optimalFirstPricePoint = simulationResults.reduce((max, p) => p.expectedProfit > max.expectedProfit ? p : max, simulationResults[0]);
        const closestEnglishPoint = simulationResults.reduce((closest, p) => {
            return Math.abs(p.bid - correctedCost) < Math.abs(closest.bid - correctedCost) ? p : closest;
        });

        return {
            firstPrice: {
                bid: optimalFirstPricePoint.bid,
                profit: optimalFirstPricePoint.expectedProfit,
                winRate: optimalFirstPricePoint.winProbability,
            },
            english: {
                bid: correctedCost,
                profit: closestEnglishPoint.expectedProfit,
                winRate: closestEnglishPoint.winProbability,
            }
        };
    }, [simulationResults, correctedCost]);

    return (
      <div onClick={() => setActiveTooltip(null)}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* --- INPUTS COLUMN --- */}
            <div className="lg:col-span-1 flex flex-col space-y-6">
                <Card>
                    <CardHeader icon={<SettingsIcon />} title="Auction Setup" />
                    <div className="p-4 space-y-4">
                        <Select 
                            id="auction-type"
                            label="Auction Type" 
                            value={auctionType} 
                            onChange={e => setAuctionType(e.target.value)} 
                            options={AUCTION_TYPES} 
                            tooltip="Select the auction format. This critically impacts bidding strategy and payment rules."
                            activeTooltip={activeTooltip}
                            setActiveTooltip={setActiveTooltip}
                        />
                         <Select 
                            id="currency-type"
                            label="Currency" 
                            value={currency} 
                            onChange={e => setCurrency(e.target.value)} 
                            options={CURRENCIES} 
                            tooltip="Select the currency for all monetary values in the tool."
                            activeTooltip={activeTooltip}
                            setActiveTooltip={setActiveTooltip}
                            displaySymbol={true}
                        />
                    </div>
                </Card>
                <Card>
                    <CardHeader icon={<DollarSignIcon />} title="My Cost Breakdown" />
                    <div className="p-4 space-y-3">
                        <Select 
                            id="project-type"
                            label="Project Type"
                            value={projectType}
                            onChange={(e) => setProjectType(e.target.value)}
                            options={PROJECT_TYPES}
                            tooltip="Select the type of project to load a default cost structure."
                            activeTooltip={activeTooltip}
                            setActiveTooltip={setActiveTooltip}
                        />
                        <div className="flex items-center">
                            <input type="checkbox" id="advanced-cost-check" checked={useAdvancedCost} onChange={(e) => setUseAdvancedCost(e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                            <label htmlFor="advanced-cost-check" className="ml-2 block text-sm text-gray-300">Use Advanced Cost Structure</label>
                        </div>
                        <hr className="border-gray-700"/>
                        {useAdvancedCost ? (
                            Object.entries(costs).map(([key, value]) => (
                                <Input 
                                    key={key} 
                                    id={`cost-${key}`}
                                    label={key.charAt(0).toUpperCase() + key.slice(1)} 
                                    value={value} 
                                    onChange={e => handleCostChange(key, e.target.value)} 
                                    unit={CURRENCIES[currency].symbol}
                                    tooltip="Enter your best cost estimate for this specific component of the project. The sum of these values forms your total estimated cost (s)."
                                    activeTooltip={activeTooltip}
                                    setActiveTooltip={setActiveTooltip}
                                />
                            ))
                        ) : (
                             <Input 
                                id="simple-cost"
                                label="Total Estimated Cost" 
                                value={simpleCost} 
                                onChange={e => setSimpleCost(Number(e.target.value))} 
                                unit={CURRENCIES[currency].symbol}
                                tooltip="Enter your single, all-in best estimate for the total project cost."
                                activeTooltip={activeTooltip}
                                setActiveTooltip={setActiveTooltip}
                            />
                        )}
                        <div className="pt-2 border-t border-gray-700">
                           <p className="text-sm text-gray-400">Total Estimated Cost:</p>
                           <p className="text-xl font-bold">{formatCurrency(myCostEstimate)}</p>
                        </div>
                    </div>
                </Card>
            </div>

            {/* --- MIDDLE COLUMN --- */}
            <div className="lg:col-span-2 flex flex-col space-y-6">
                <Card>
                    <CardHeader icon={<UsersIcon />} title="Competitor Profiles" subtitle={`Modeling ${numBidders - 1} rivals.`} />
                    <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                        {competitors.map((comp, index) => (
                            <div key={index} className="p-3 bg-gray-900/50 rounded-lg border border-gray-700 relative">
                                <button onClick={() => removeCompetitorProfile(index)} className="absolute top-2 right-2 text-gray-500 hover:text-red-500"><XCircleIcon /></button>
                                <Select 
                                    id={`comp-type-${index}`}
                                    label="Type" 
                                    value={comp.type} 
                                    onChange={e => handleCompetitorChange(index, 'type', e.target.value)} 
                                    options={COMPETITOR_TYPES}
                                    tooltip="Choose a behavioral archetype for this group of competitors. This determines their general markup strategy in the simulation."
                                    activeTooltip={activeTooltip}
                                    setActiveTooltip={setActiveTooltip}
                                />
                                <Input 
                                    id={`comp-count-${index}`}
                                    label="Count" 
                                    value={comp.count} 
                                    onChange={e => handleCompetitorChange(index, 'count', e.target.value)}
                                    tooltip="Enter the number of competitors that fit this profile. The total number of bidders (n) is a key factor in both the Winner's Curse and optimal bidding formulas."
                                    activeTooltip={activeTooltip}
                                    setActiveTooltip={setActiveTooltip}
                                />
                                <Input 
                                    id={`comp-avg-cost-${index}`}
                                    label="Avg. Cost" 
                                    value={comp.costMean} 
                                    onChange={e => handleCompetitorChange(index, 'costMean', e.target.value)} 
                                    unit={CURRENCIES[currency].symbol}
                                    tooltip="Your estimate of the average project cost for this type of competitor. This reflects their efficiency, scale, and supply chain."
                                    activeTooltip={activeTooltip}
                                    setActiveTooltip={setActiveTooltip}
                                />
                                <Input 
                                    id={`comp-std-dev-${index}`}
                                    label="Cost Std. Dev." 
                                    value={comp.costStdDev} 
                                    onChange={e => handleCompetitorChange(index, 'costStdDev', e.target.value)} 
                                    unit={CURRENCIES[currency].symbol}
                                    tooltip="How much this competitor type's costs vary around their average. A low value means they are very consistent; a high value means their costs are less predictable."
                                    activeTooltip={activeTooltip}
                                    setActiveTooltip={setActiveTooltip}
                                />
                            </div>
                        ))}
                         <button onClick={addCompetitorProfile} className="w-full text-blue-400 hover:text-blue-300 flex items-center justify-center p-2 border-2 border-dashed border-gray-600 rounded-lg hover:border-blue-500 transition md:col-span-2">
                            <PlusCircleIcon className="mr-2"/> Add Competitor Group
                        </button>
                    </div>
                </Card>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Card>
                        <CardHeader icon={<SlidersHorizontalIcon />} title="Winner's Curse & Bidding" />
                        <div className="p-4 space-y-3">
                            <Input 
                                id="cost-uncertainty"
                                label="Cost Uncertainty (ε)" 
                                value={costUncertainty} 
                                onChange={e => setCostUncertainty(Number(e.target.value))} 
                                unit={CURRENCIES[currency].symbol}
                                tooltip="This is the most critical input for the Winner's Curse. It represents the potential range of error in your cost estimate (e.g., due to market volatility, unforeseen risks). A higher value means more uncertainty and a larger upward correction to your cost basis to avoid making a loss. A good starting point is 5-10% of your total cost."
                                activeTooltip={activeTooltip}
                                setActiveTooltip={setActiveTooltip}
                            />
                            <div className="bg-yellow-900/30 p-4 rounded-lg border border-yellow-500/30">
                                <p className="text-sm text-yellow-300 flex items-center"><span className="ml-2">Corrected Cost</span></p>
                                <p className="text-xs text-gray-400 mt-1">Cost adjusted for Winner's Curse.</p>
                                <p className="text-2xl font-bold text-white mt-2">{formatCurrency(correctedCost)}</p>
                            </div>
                        </div>
                    </Card>
                    <Card>
                        <CardHeader icon={<AwardIcon />} title="Multi-Attribute Scoring" />
                        <div className="p-4 space-y-3">
                            {/* How it works toggle */}
                            <button 
                                onClick={() => setShowMultiAttributeHelp(!showMultiAttributeHelp)}
                                className="w-full text-left text-sm text-blue-400 hover:text-blue-300 flex items-center justify-between p-2 bg-gray-900/30 rounded border border-gray-700 hover:border-blue-500 transition"
                            >
                                <span>How Multi-Attribute Scoring Works</span>
                                <span className={`transform transition-transform ${showMultiAttributeHelp ? 'rotate-180' : ''}`}>▼</span>
                            </button>

                            {showMultiAttributeHelp && (
                                <div className="bg-blue-900/20 border border-blue-700/50 rounded-lg p-3 mb-4 space-y-3">
                                    <p className="text-xs text-gray-300 mb-2">
                                        In procurement auctions, buyers often evaluate proposals on multiple criteria beyond just price. 
                                        Your <strong>Total Score</strong> determines your ranking against competitors.
                                    </p>
                                    <div className="bg-gray-800 p-2 rounded text-center text-xs">
                                        <span className="text-white font-mono">
                                            Total Score = (Price Score × Price Weight) + (Tech Score × Tech Weight) + (Experience Score × Exp Weight)
                                        </span>
                                    </div>
                                    <p className="text-xs text-gray-400">
                                        <strong>Price Score:</strong> Calculated automatically - lower bids get higher price scores (0-100).
                                        <br/>
                                        <strong>Non-Price Scores:</strong> Set by buyer based on your proposal quality (0-100).
                                    </p>
                                    
                                    {/* Sample Scoring inside How it Works */}
                                    <div className="bg-gray-900/50 p-3 rounded-lg border border-gray-700">
                                        <h4 className="text-sm font-semibold text-green-400 mb-2">Sample Scoring</h4>
                                        <div className="text-xs text-gray-300 space-y-1">
                                            <div className="grid grid-cols-2 gap-2">
                                                <div>
                                                    <strong>Your Bid:</strong> {formatCurrency(myCostEstimate)}<br/>
                                                    <strong>Lowest Bid:</strong> {formatCurrency(myCostEstimate * 0.95)}<br/>
                                                    <strong>Highest Bid:</strong> {formatCurrency(myCostEstimate * 1.1)}
                                                </div>
                                                <div>
                                                    <strong>Your Price Score:</strong> {Math.round(((myCostEstimate * 1.1 - myCostEstimate) / (myCostEstimate * 1.1 - myCostEstimate * 0.95)) * 100)}/100<br/>
                                                    <strong>Tech Score:</strong> {multiAttribute.techScore}/100<br/>
                                                    <strong>Experience Score:</strong> {multiAttribute.expScore}/100
                                                </div>
                                            </div>
                                            <div className="border-t border-gray-600 pt-2 mt-2">
                                                <strong className="text-yellow-400">
                                                    Your Total Score: {
                                                        (
                                                            (((myCostEstimate * 1.1 - myCostEstimate) / (myCostEstimate * 1.1 - myCostEstimate * 0.95)) * 100 * multiAttribute.priceWeight / 100) +
                                                            (multiAttribute.techScore * (100 - multiAttribute.priceWeight) / 200) +
                                                            (multiAttribute.expScore * (100 - multiAttribute.priceWeight) / 200)
                                                        ).toFixed(1)
                                                    }/100
                                                </strong>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <Input 
                                id="price-weight"
                                label="Price Weight (%)" 
                                value={multiAttribute.priceWeight} 
                                onChange={e => setMultiAttribute(p => ({...p, priceWeight: Number(e.target.value)}))} 
                                unit="%"
                                tooltip="Percentage weight given to price in the final evaluation. E.g., 80% means price is very important. The remaining 20% goes to technical and experience factors. Higher price weight favors the lowest bidders."
                                activeTooltip={activeTooltip}
                                setActiveTooltip={setActiveTooltip}
                            />
                            <Input 
                                id="tech-score"
                                label="My Technical Score" 
                                value={multiAttribute.techScore} 
                                onChange={e => setMultiAttribute(p => ({...p, techScore: Number(e.target.value)}))} 
                                unit="/ 100"
                                tooltip="Your expected technical evaluation score from the buyer (0-100). This reflects the quality of your technical proposal, solution design, equipment specifications, and project approach. Be realistic based on your actual capabilities."
                                activeTooltip={activeTooltip}
                                setActiveTooltip={setActiveTooltip}
                            />
                            <Input 
                                id="exp-score"
                                label="My Experience Score" 
                                value={multiAttribute.expScore} 
                                onChange={e => setMultiAttribute(p => ({...p, expScore: Number(e.target.value)}))} 
                                unit="/ 100"
                                tooltip="Your expected experience evaluation score from the buyer (0-100). This includes your company's track record, similar project experience, financial stability, and reputation. Consider your actual experience level compared to competitors."
                                activeTooltip={activeTooltip}
                                setActiveTooltip={setActiveTooltip}
                            />
                        </div>
                    </Card>
                </div>
                 <Card>
                    <CardHeader icon={<LightbulbIcon />} title="Auction Strategy Guide" />
                    <div className="p-4 text-sm">
                        {auctionType === 'FIRST_PRICE' && (
                            <div>
                                <h4 className="font-bold text-white">First-Price Sealed-Bid Procurement Strategy</h4>
                                <p className="text-gray-300 mt-2">You submit one sealed bid to a buyer. The lowest bidder wins and receives payment equal to their bid. In procurement, you must balance being competitive (low bid) with maintaining profitability.</p>
                                <ul className="list-disc list-inside space-y-1 mt-2 text-gray-300">
                                    <li><strong>Winner's Curse Adjustment:</strong> Your cost estimate is corrected upward by the formula: s + ((n-1)/(n+1)) × ε to avoid winning unprofitable contracts.</li>
                                    <li><strong>Strategic Bidding:</strong> Bid slightly above your Corrected Cost. The optimal markup depends on competition level - fewer competitors allow higher markups.</li>
                                    <li><strong>Recommendation:</strong> Use the <span className="font-bold text-green-400">Optimal Bid</span> from simulation that maximizes expected profit while accounting for win probability.</li>
                                </ul>
                            </div>
                        )}
                        {auctionType === 'ENGLISH' && (
                             <div>
                                <h4 className="font-bold text-white">English Reverse Auction Procurement Strategy</h4>
                                <p className="text-gray-300 mt-2">An open, descending-price auction where bidders drop out as prices fall. The last remaining bidder wins at the price where the second-to-last bidder exited. This encourages truthful bidding.</p>
                                <ul className="list-disc list-inside space-y-1 mt-2 text-gray-300">
                                    <li><strong>Dominant Strategy:</strong> Bid truthfully - stay in until the price reaches your Corrected Cost, then exit immediately.</li>
                                    <li><strong>Winner's Curse Protection:</strong> Your Corrected Cost already includes the adjustment: s + ((n-1)/(n+1)) × ε</li>
                                    <li><strong>Recommendation:</strong> Set your <span className="font-bold text-blue-400">Exit Price</span> at your Corrected Cost. Never bid below this threshold.</li>
                                </ul>
                            </div>
                        )}
                         {auctionType === 'DUTCH' && (
                             <div>
                                <h4 className="font-bold text-white">Dutch Reverse Auction Procurement Strategy</h4>
                                <p className="text-gray-300 mt-2">An ascending-price auction where the buyer starts low and raises the price until someone accepts. The first bidder to accept wins at that price. Strategically equivalent to First-Price Sealed-Bid.</p>
                                <ul className="list-disc list-inside space-y-1 mt-2 text-gray-300">
                                     <li><strong>Pre-Decision Required:</strong> Determine your acceptance price before the auction begins, as you must act quickly when that price is called.</li>
                                    <li><strong>Strategic Equivalence:</strong> Use the same strategy as First-Price - bid above your Corrected Cost with optimal markup based on competition.</li>
                                    <li><strong>Recommendation:</strong> Accept when the clock reaches your <span className="font-bold text-green-400">Optimal Bid</span> from the simulation analysis.</li>
                                </ul>
                            </div>
                        )}
                         {auctionType === 'VICKREY' && (
                             <div>
                                <h4 className="font-bold text-white">Vickrey Reverse Auction Procurement Strategy</h4>
                                <p className="text-gray-300 mt-2">You submit one sealed bid. The lowest bidder wins but receives payment equal to the second-lowest bid. This second-price rule makes truthful bidding the dominant strategy.</p>
                                <ul className="list-disc list-inside space-y-1 mt-2 text-gray-300">
                                    <li><strong>Truthful Bidding:</strong> Submit exactly your Corrected Cost. Any deviation from this reduces your expected payoff.</li>
                                    <li><strong>Payment Protection:</strong> You're paid the second-lowest bid, which protects you from the Winner's Curse while encouraging honest bidding.</li>
                                    <li><strong>Recommendation:</strong> Bid your <span className="font-bold text-blue-400">Corrected Cost</span> - this maximizes expected profit under the incentive-compatible mechanism.</li>
                                </ul>
                            </div>
                        )}
                    </div>
                </Card>
            </div>
        </div>
        <div className="mt-6">
             <Card>
                <CardHeader icon={<BarChartIcon />} title="Simulation Analysis & Strategic Summary" />
                <div className="p-4">
                    <div className="mb-6">
                        <Button onClick={handleRunSimulation} disabled={isLoading}>
                            {isLoading ? 'Simulating...' : 'Run Advanced Simulation'}
                        </Button>
                    </div>
                    <p className="text-sm text-gray-400 mb-4">Run thousands of virtual auctions to analyze the trade-off between your bid price, win probability, and expected profit. The chart below shows the results of these simulations, helping you visualize risk and reward to make a final, data-driven decision.</p>
                     <LineChart 
                        data={simulationResults}
                        xAxisKey="bid"
                        yAxisKeys={[
                            { key: 'winProbability', name: 'Win Probability', color: '#3b82f6' },
                            { key: 'expectedProfit', name: 'Expected Profit', color: '#22c55e' }
                        ]}
                        auctionType={auctionType}
                        correctedCost={correctedCost}
                        currency={CURRENCIES[currency]}
                    />
                    {simulationResults && (
                        <div className="mt-6">
                            <h4 className="text-lg font-semibold text-white mb-2">Strategic Recommendation</h4>
                             <div className="overflow-x-auto">
                                <table className="w-full text-sm text-left text-gray-300">
                                    <thead className="text-xs text-gray-400 uppercase bg-gray-700/50">
                                        <tr>
                                            <th scope="col" className="px-6 py-3">Current Auction Strategy</th>
                                            <th scope="col" className="px-6 py-3">Recommended Bid Price</th>
                                            <th scope="col" className="px-6 py-3">Expected Profit</th>
                                            <th scope="col" className="px-6 py-3">Win Probability</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(auctionType === 'FIRST_PRICE' || auctionType === 'DUTCH') ? (
                                            <tr>
                                                <th scope="row" className="px-6 py-4 font-medium text-white whitespace-nowrap">
                                                    {auctionType === 'FIRST_PRICE' ? 'First-Price Sealed Bid' : 'Dutch Reverse'} (Maximize Profit)
                                                </th>
                                                <td className="px-6 py-4">{formatCurrency(summaryData.firstPrice.bid)}</td>
                                                <td className="px-6 py-4 text-green-400">{formatCurrency(summaryData.firstPrice.profit)}</td>
                                                <td className="px-6 py-4 text-blue-400">{(summaryData.firstPrice.winRate * 100).toFixed(1)}%</td>
                                            </tr>
                                        ) : (
                                            <tr>
                                                <th scope="row" className="px-6 py-4 font-medium text-white whitespace-nowrap">
                                                    {auctionType === 'ENGLISH' ? 'English Reverse' : 'Vickrey Reverse'} (Truthful Bidding)
                                                </th>
                                                <td className="px-6 py-4">{formatCurrency(summaryData.english.bid)}</td>
                                                <td className="px-6 py-4 text-green-400">{formatCurrency(summaryData.english.profit)}</td>
                                                <td className="px-6 py-4 text-blue-400">{(summaryData.english.winRate * 100).toFixed(1)}%</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            </Card>
        </div>
      </div>
    );
}
